import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { QUOTES_KEY } from './types';
import { DialogLayer } from '../ui/Dialog';
import { sameValues, useUnsavedGuard } from '../ui/unsaved';

type QuoteSuppliers = { suppliers: Array<{ id: string; name: string; active: boolean }>; version: number };

/**
 * "…" → "Dostawcy dla kosztorysu" (owner 2026-10-21 00:32): which suppliers' catalogs this quote searches and adds
 * from. All on / all off; an inactive supplier is not searched. Saved for the whole quote (every version).
 */
export function SuppliersDialog({ quoteId, onClose }: { quoteId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const data = useQuery({ queryKey: [...QUOTES_KEY, 'suppliers', quoteId], queryFn: () => api<QuoteSuppliers>(`/api/quotes/quotes/${quoteId}/suppliers`) });
  const [active, setActive] = useState<Set<string> | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (data.data && active === null) setActive(new Set(data.data.suppliers.filter((s) => s.active).map((s) => s.id)));
  }, [data.data, active]);
  const list = data.data?.suppliers ?? [];
  const on = active ?? new Set<string>();
  const toggle = (id: string) => setActive(new Set(on.has(id) ? [...on].filter((x) => x !== id) : [...on, id]));

  async function save(): Promise<boolean> {
    if (!data.data) return false;
    setBusy(true);
    try {
      await api(`/api/quotes/quotes/${quoteId}/suppliers`, { method: 'PUT', body: { inactiveSupplierIds: list.filter((s) => !on.has(s.id)).map((s) => s.id), version: data.data.version } });
      await queryClient.invalidateQueries({ queryKey: QUOTES_KEY });
      onClose();
      return true;
    } catch (e) {
      setError(e instanceof ApiError && e.status === 409 ? 'Kosztorys zmienił się w międzyczasie — zamknij okno i spróbuj jeszcze raz.' : e instanceof ApiError ? e.message : 'Nie udało się zapisać');
      return false;
    } finally {
      setBusy(false);
    }
  }
  // owner 2026-10-23 21:14: Escape or leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const wasOn = list.filter((s) => s.active).map((s) => s.id).sort();
  const guardRef = useUnsavedGuard({ dirty: active !== null && !sameValues([...active].sort(), wasOn), save });

  return (
    <div className="dialog-backdrop">
      <div ref={guardRef} className="dialog panel q-suppliers-dialog" role="dialog" aria-modal="true" aria-labelledby="q-suppliers-title" data-feature-id="quotes.suppliers">
        <DialogLayer onClose={onClose} busy={busy} />
        <h2 id="q-suppliers-title">Dostawcy dla kosztorysu</h2>
        <p className="hint">Zaznaczeni dostawcy są przeszukiwani przy dodawaniu pozycji („Korzystaj z katalogów dostawców”). Wybór dotyczy całego kosztorysu.</p>
        {data.isLoading && <p className="muted">Wczytywanie…</p>}
        {data.error && <p className="alert" role="alert">{data.error instanceof ApiError ? data.error.message : 'Nie udało się wczytać dostawców'}</p>}
        {data.data && list.length === 0 && <p className="muted">Nie ma jeszcze dostawców — dodaj kontrahenta z rodzajem „dostawca”.</p>}
        {list.length > 0 && (
          <>
            <div className="actions q-suppliers-all">
              <button className="btn small" type="button" onClick={() => setActive(new Set(list.map((s) => s.id)))}>
                Zaznacz wszystkich
              </button>
              <button className="btn small" type="button" onClick={() => setActive(new Set())}>
                Odznacz wszystkich
              </button>
            </div>
            <ul className="q-suppliers-list">
              {list.map((s) => (
                <li key={s.id}>
                  <label className="check">
                    <input type="checkbox" checked={on.has(s.id)} onChange={() => toggle(s.id)} />
                    <span>{s.name}</span>
                  </label>
                </li>
              ))}
            </ul>
          </>
        )}
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="button" disabled={busy || !data.data} onClick={() => void save()}>
            Zapisz
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
